import { useCallback, useEffect, useState } from 'react';
import { Collapse, makeStyles } from '@material-ui/core';
import { t } from '@lingui/macro';
import { isEmpty, pick } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';

import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useDashboardContext } from './DashboardContext';
import FormSection from '../../form/FormSection';
import NgpToggle from '../../form/NgpToggle';
import { useDataService } from '../../../services/ApiDataService';
import { useMenu } from '../../../stores/DashboardMenuStore';
import ListSelectorField from '../../form/ListSelectorField';
import { DEFAULT_GROUP_UUID } from '../../pages-admin/groups/GroupsAdminConstants';
import useToasts, { SUCCESS, ERROR } from '../../../stores/ToastStore';
import { createAlertConfig } from '../../../stores/AlertsStore';
import { useUserGroups } from '../../../stores/SessionStore';
import { DASHBOARD_SHARE_GROUPS_COLUMNS } from '../../pages-admin/dashboards/DashboardsAdminConstants';

const useStyles = makeStyles({
    modalContentWrapper: {
        width: '100%',
        maxHeight: 800,
    },
    selectorWrapper: {
        marginTop: 16,
    },
});

export function ShareDashboardDialog({ open, setOpen }) {
    const styles = useStyles();
    const [availableGroups, setAvailableGroups] = useState([]);
    const [, dashboardMenuActions] = useMenu();
    const { dashboard, dashboardActions } = useDashboardContext();
    const [, uiToastActions] = useToasts();
    const [userGroups] = useUserGroups();
    const dataService = useDataService();
    const methods = useForm({ ...USE_FORM_DEFAULT_OPTIONS, defaultValues: dashboard });
    const { handleSubmit, setValue, reset, watch } = methods;
    const isPrivate = watch('private');

    useEffect(() => {
        (async () => {
            const availableGroupsData = await dataService.get('/ipm/v1/dashboards/availableGroups');
            setAvailableGroups(availableGroupsData);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        reset(dashboard);
    }, [dashboard, reset]);

    const handleFormSubmit = useCallback(
        (submitData) => {
            const dataToSave = pick({ ...dashboard, ...submitData }, ['groups', 'private']);
            return dashboardActions
                .share(dashboard._id, dataToSave)
                .then(() => {
                    dashboardMenuActions.load();
                    setOpen(false);
                    uiToastActions.createToast(
                        createAlertConfig(
                            SUCCESS,
                            dataToSave.private
                                ? t`Successfully set dashboard to private.`
                                : t`Successfully shared dashboard.`
                        )
                    );
                })
                .catch(() => {
                    uiToastActions.createToast(createAlertConfig(ERROR, t`Failed to update dashboard share settings.`));
                });
        },
        [dashboard, dashboardMenuActions, dashboardActions, uiToastActions, setOpen]
    );

    const getUserDefaultSelection = useCallback(() => {
        if (userGroups.includes(DEFAULT_GROUP_UUID)) {
            setValue('groups', [DEFAULT_GROUP_UUID]);
            return [DEFAULT_GROUP_UUID];
        }

        setValue('groups', []);
        return [];
    }, [userGroups, setValue]);

    return (
        <NgpTwoButtonDialog
            title={t`Share Dashboard`}
            open={open}
            setOpen={setOpen}
            onConfirm={handleSubmit((data) => handleFormSubmit(data))}
        >
            <div className={styles.modalContentWrapper}>
                <FormProvider {...methods}>
                    <NgpForm initialized showSave={false} showRequiredFieldsTip={false}>
                        <FormSection title={t`Dashboard Visiblity`}>
                            <NgpToggle fieldName="private" label={t`Private`} />
                        </FormSection>
                        <Collapse in={!isPrivate}>
                            <FormSection title={t`Share With Groups`}>
                                <div>
                                    <ListSelectorField
                                        gridColumns={DASHBOARD_SHARE_GROUPS_COLUMNS}
                                        dataArray={isPrivate ? [] : availableGroups}
                                        fieldName="groups"
                                        dataArraySearchFields={['groupName']}
                                        dbSelected={
                                            !isEmpty(dashboard?.groups) ? dashboard.groups : getUserDefaultSelection()
                                        }
                                        addButtonText={t`Add Groups`}
                                        removeButtonText={t`Remove`}
                                        selectedEmptyGridText={t`This Dashboard has not been shared with any of your available Groups`}
                                        visibleRows={5}
                                    />
                                </div>
                            </FormSection>
                        </Collapse>
                    </NgpForm>
                </FormProvider>
            </div>
        </NgpTwoButtonDialog>
    );
}
